import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FileText, FileDown, Printer, XCircle, MailWarning, RefreshCw } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

export const NFE_STATUS = {
  autorizado: { label: 'NF-e autorizada', cls: 'bg-emerald-100 text-emerald-700' },
  processando_autorizacao: { label: 'NF-e processando', cls: 'bg-blue-100 text-blue-700' },
  erro_autorizacao: { label: 'NF-e rejeitada', cls: 'bg-red-100 text-red-700' },
  denegado: { label: 'NF-e denegada', cls: 'bg-red-100 text-red-700' },
  cancelado: { label: 'NF-e cancelada', cls: 'bg-slate-200 text-slate-700' },
};

export const NfeBadge = ({ nfe }) => {
  if (!nfe?.status) return null;
  const st = NFE_STATUS[nfe.status] || { label: `NF-e ${nfe.status}`, cls: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ml-1 ${st.cls}`} title={nfe.mensagem_sefaz || ''} data-testid="nfe-badge">
      {st.label}{nfe.number ? ` nº ${nfe.number}` : ''}{nfe.cces?.length ? ` · CC-e ${nfe.cces.length}` : ''}
    </span>
  );
};

const Modal = ({ title, onClose, children, testId, wide }) => (
  <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" {...overlayClose(onClose)} data-testid={testId}>
    <div className={`bg-white rounded-md p-5 w-full ${wide ? 'max-w-3xl' : 'max-w-md'} max-h-[92vh] overflow-y-auto shadow-2xl`} onClick={(e) => e.stopPropagation()}>
      <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
      {children}
    </div>
  </div>
);

const F = ({ label, k, dest, setDest, ph, req, maxLength, testId, span }) => (
  <div className={`space-y-1 ${span || ''}`}>
    <Label className="text-xs">{label}{req ? ' *' : ''}</Label>
    <Input value={dest[k] || ''} onChange={(e) => setDest({ ...dest, [k]: e.target.value })} placeholder={ph} maxLength={maxLength} data-testid={testId || `nfe-dest-${k}`} />
  </div>
);

// Modal de emissão: confirma/completa o destinatário (pré-preenchido do cliente) e transmite
export const NfeEmitModal = ({ sale, onClose, onDone }) => {
  const [dest, setDest] = useState(null);
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  useEffect(() => {
    axios.get(`${API}/nfe/destinatario/${sale.id}`, cfg)
      .then((r) => { setDest({ save_to_customer: true, ...r.data.destinatario }); setMeta(r.data); })
      .catch((e) => setErr(e.response?.data?.detail || 'Falha ao carregar destinatário'));
  }, [sale.id]);

  const emit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/nfe/issue/${sale.id}`, dest, cfg);
      setResult(r.data);
      if (r.data.status === 'autorizado') toast.success(`NF-e nº ${r.data.number} autorizada`);
      else if (r.data.status === 'erro_autorizacao') toast.error('NF-e rejeitada pela SEFAZ');
      onDone?.(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Erro ao emitir NF-e'); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Emitir NF-e (modelo 55) — Venda #${sale.sale_number}`} onClose={onClose} testId="nfe-emit-modal" wide>
      {result ? (
        <div className="space-y-3" data-testid="nfe-emit-result">
          <div className={`rounded-md border-2 p-3 ${result.status === 'autorizado' ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
            <p className="font-bold">{NFE_STATUS[result.status]?.label || result.status}{result.number ? ` nº ${result.number} · série ${result.serie}` : ''}</p>
            {result.access_key && <p className="text-xs font-mono break-all mt-1">Chave: {result.access_key}</p>}
            {result.protocolo && <p className="text-xs">Protocolo: {result.protocolo}</p>}
            {result.mensagem_sefaz && <p className="text-sm mt-1" data-testid="nfe-sefaz-msg">SEFAZ: {result.mensagem_sefaz}</p>}
            {Array.isArray(result.erros) && result.erros.map((e, i) => <p key={i} className="text-xs text-red-700">• {e.mensagem || JSON.stringify(e)}</p>)}
          </div>
          <div className="flex justify-end gap-2">
            {result.status === 'autorizado' && <Button onClick={() => window.open(`${API}/nfe/danfe/${sale.id}`, '_blank')} className="bg-slate-900 hover:bg-black" data-testid="nfe-result-danfe"><Printer size={14} className="mr-1" /> Imprimir DANFE</Button>}
            {result.status === 'erro_autorizacao' && <Button variant="outline" onClick={() => setResult(null)} data-testid="nfe-result-retry">Corrigir e reenviar</Button>}
            <Button variant="outline" onClick={onClose}>Fechar</Button>
          </div>
        </div>
      ) : !dest ? <p className="text-sm text-slate-500">{err || 'Carregando…'}</p> : (
        <>
          <p className="text-xs text-slate-500 mb-3">Ambiente <b>{meta.ambiente}</b> · Série <b>{meta.serie}</b> · Próximo nº <b>{meta.numero}</b>. Total R$ {Number(sale.total).toFixed(2)}. O destinatário (CPF/CNPJ e endereço completo) é obrigatório na NF-e.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Nome / Razão social" k="nome" dest={dest} setDest={setDest} req span="md:col-span-2" />
            <F label="CPF / CNPJ" k="documento" dest={dest} setDest={setDest} req ph="somente números" />
            <div className="space-y-1">
              <Label className="text-xs">Contribuinte ICMS *</Label>
              <select value={dest.indicador_ie || '9'} onChange={(e) => setDest({ ...dest, indicador_ie: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="nfe-dest-indicador_ie">
                <option value="9">9 — Não contribuinte (consumidor)</option>
                <option value="1">1 — Contribuinte ICMS (com IE)</option>
                <option value="2">2 — Contribuinte isento de IE</option>
              </select>
            </div>
            {dest.indicador_ie === '1' && <F label="Inscrição Estadual" k="ie" dest={dest} setDest={setDest} req />}
            <F label="Logradouro" k="logradouro" dest={dest} setDest={setDest} req span="md:col-span-2" />
            <F label="Número" k="numero" dest={dest} setDest={setDest} ph="S/N" />
            <F label="Bairro" k="bairro" dest={dest} setDest={setDest} req />
            <F label="Município" k="municipio" dest={dest} setDest={setDest} req />
            <F label="Cód. IBGE município" k="codigo_municipio" dest={dest} setDest={setDest} ph="ex.: 5002704" maxLength={7} />
            <F label="UF" k="uf" dest={dest} setDest={setDest} req maxLength={2} ph="MS" />
            <F label="CEP" k="cep" dest={dest} setDest={setDest} req ph="79000000" />
            <F label="Telefone" k="telefone" dest={dest} setDest={setDest} />
            <F label="E-mail" k="email" dest={dest} setDest={setDest} span="md:col-span-2" />
            <F label="Natureza da operação" k="natureza_operacao" dest={dest} setDest={setDest} ph="VENDA DE MERCADORIA" />
            <F label="Observações (info. complementares)" k="observacoes" dest={dest} setDest={setDest} span="md:col-span-3" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 mt-3">
            <input type="checkbox" checked={!!dest.save_to_customer} onChange={(e) => setDest({ ...dest, save_to_customer: e.target.checked })} data-testid="nfe-dest-save" />
            Atualizar o cadastro do cliente com estes dados
          </label>
          {err && <p className="text-sm text-red-600 mt-2" data-testid="nfe-emit-error">{err}</p>}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={emit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="nfe-emit-confirm">
              <FileText size={14} className={`mr-1 ${busy ? 'animate-pulse' : ''}`} /> {busy ? 'Transmitindo à SEFAZ…' : 'Transmitir NF-e'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

const TextModal = ({ title, hint, min, max, placeholder, confirmLabel, onConfirm, onClose, danger, testId }) => {
  const [txt, setTxt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    try { await onConfirm(txt); onClose(); } catch (e) { setErr(e.response?.data?.detail || 'Falha na operação'); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} testId={testId}>
      <p className="text-xs text-slate-500 mb-3">{hint}</p>
      <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={4} className="w-full border border-slate-300 rounded-md p-2 text-sm" placeholder={placeholder} data-testid={`${testId}-text`} />
      <p className="text-[11px] text-slate-400">{txt.trim().length}/{max}</p>
      {err && <p className="text-sm text-red-600 mt-1" data-testid={`${testId}-error`}>{err}</p>}
      <div className="flex justify-end gap-2 mt-3">
        <Button variant="outline" onClick={onClose}>Voltar</Button>
        <Button onClick={go} disabled={busy || txt.trim().length < min} className={danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} data-testid={`${testId}-confirm`}>{busy ? 'Enviando à SEFAZ…' : confirmLabel}</Button>
      </div>
    </Modal>
  );
};

// Botão "Emitir NF-e" + ações (DANFE, XML, cancelar, CC-e, reconsultar) por venda
export const NfeActions = ({ sale, onChanged, compact }) => {
  const [emitOpen, setEmitOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cceOpen, setCceOpen] = useState(false);
  const nf = sale.nfe;
  const isCancelled = sale.status === 'cancelled';
  const authorized = nf?.status === 'autorizado';
  const canEmit = !isCancelled && !['autorizado', 'processando_autorizacao', 'cancelado'].includes(nf?.status);
  const hoursLeft = authorized ? Math.max(0, 24 - (Date.now() - new Date(nf.issued_at || nf.updated_at).getTime()) / 3600000) : 0;
  const refresh = async () => {
    try { await axios.get(`${API}/nfe/status/${sale.id}`, cfg); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || 'Falha ao consultar'); }
  };
  const btn = 'text-xs inline-flex items-center gap-0.5 hover:underline';
  return (
    <span className="inline-flex flex-wrap items-center gap-2 ml-1">
      {canEmit && (
        <button onClick={() => setEmitOpen(true)} className={`${btn} text-indigo-700 font-bold`} title={nf?.status === 'erro_autorizacao' ? `Rejeitada: ${nf.mensagem_sefaz || ''} — corrigir e reenviar` : 'Emitir NF-e modelo 55 para esta venda'} data-testid={`nfe-emit-${sale.id}`}>
          <FileText size={14} /> {nf?.status === 'erro_autorizacao' ? 'Reenviar NF-e' : 'Emitir NF-e'}
        </button>
      )}
      {nf?.status === 'processando_autorizacao' && <button onClick={refresh} className={`${btn} text-blue-700`} data-testid={`nfe-refresh-${sale.id}`}><RefreshCw size={14} /> Consultar</button>}
      {authorized && nf.danfe_path && <a href={`${API}/nfe/danfe/${sale.id}`} target="_blank" rel="noreferrer" className={`${btn} text-slate-700`} title="Imprimir DANFE (PDF A4)" data-testid={`nfe-danfe-${sale.id}`}><Printer size={14} /> DANFE</a>}
      {(authorized || nf?.status === 'cancelado') && nf.xml_path && <a href={`${API}/nfe/xml/${sale.id}`} className={`${btn} text-slate-600`} title="Baixar XML autorizado" data-testid={`nfe-xml-${sale.id}`}><FileDown size={14} /> XML</a>}
      {authorized && hoursLeft > 0 && !compact && <button onClick={() => setCancelOpen(true)} className={`${btn} text-red-600`} title={`Cancelar NF-e (restam ${Math.floor(hoursLeft)}h)`} data-testid={`nfe-cancel-${sale.id}`}><XCircle size={14} /> Cancelar NF-e</button>}
      {authorized && !compact && <button onClick={() => setCceOpen(true)} className={`${btn} text-amber-700`} title="Carta de Correção Eletrônica" data-testid={`nfe-cce-${sale.id}`}><MailWarning size={14} /> CC-e</button>}
      {nf?.status === 'erro_autorizacao' && nf.mensagem_sefaz && <span className="text-[11px] text-red-700 block w-full" data-testid={`nfe-reject-msg-${sale.id}`}>SEFAZ: {nf.mensagem_sefaz}</span>}
      {emitOpen && <NfeEmitModal sale={sale} onClose={() => setEmitOpen(false)} onDone={() => onChanged?.()} />}
      {cancelOpen && (
        <TextModal title={`Cancelar NF-e nº ${nf.number}`} hint={`Prazo legal: 24h após a autorização (restam ${Math.floor(hoursLeft)}h). A justificativa (15–255 caracteres) é enviada à SEFAZ.`} min={15} max={255}
          placeholder="Ex.: Nota emitida com dados incorretos do destinatário" confirmLabel="Confirmar cancelamento" danger testId="nfe-cancel-modal" onClose={() => setCancelOpen(false)}
          onConfirm={async (t) => { await axios.delete(`${API}/nfe/cancel/${sale.id}`, { data: { justificativa: t }, ...cfg }); toast.success('NF-e cancelada'); onChanged?.(); }} />
      )}
      {cceOpen && (
        <TextModal title={`Carta de Correção — NF-e nº ${nf.number}`} hint="Corrige erros que não alterem valores, destinatário, data ou impostos (15–1000 caracteres)." min={15} max={1000}
          placeholder="Ex.: Correção da descrição do item 1: onde se lê X, leia-se Y" confirmLabel="Enviar CC-e" testId="nfe-cce-modal" onClose={() => setCceOpen(false)}
          onConfirm={async (t) => { await axios.post(`${API}/nfe/cce/${sale.id}`, { correcao: t }, cfg); toast.success('Carta de correção registrada'); onChanged?.(); }} />
      )}
    </span>
  );
};

// Botão no cadastro do cliente: escolhe uma venda do cliente e emite a NF-e
export const CustomerNfeButton = ({ customer }) => {
  const [open, setOpen] = useState(false);
  const [sales, setSales] = useState(null);
  const [pick, setPick] = useState(null);
  const load = () => axios.get(`${API}/nfe/customer/${customer.id}/sales`, cfg).then((r) => setSales(r.data)).catch(() => setSales([]));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-indigo-700 hover:text-indigo-900 flex items-center gap-1 text-sm font-medium" title="Emitir NF-e para uma venda deste cliente" data-testid={`customer-nfe-btn-${customer.id}`}><FileText size={16} /> NF-e</button>
      {open && !pick && (
        <Modal title={`Emitir NF-e — ${customer.name}`} onClose={() => setOpen(false)} testId="customer-nfe-modal">
          <p className="text-xs text-slate-500 mb-2">Escolha a venda que será faturada na NF-e (modelo 55).</p>
          {sales === null ? <p className="text-sm text-slate-500">Carregando…</p> : sales.length === 0 ? <p className="text-sm text-slate-600" data-testid="customer-nfe-empty">Nenhuma venda deste cliente disponível para NF-e.</p> : (
            <ul className="divide-y divide-slate-200 max-h-80 overflow-y-auto">
              {sales.map((s) => (
                <li key={s.id} className="py-2 flex items-center justify-between gap-2">
                  <div className="text-sm"><b>#{s.sale_number}</b> · {new Date(s.created_at).toLocaleDateString('pt-BR')} · R$ {Number(s.total).toFixed(2)}
                    {s.nfe_status === 'erro_autorizacao' && <span className="ml-1 text-[10px] px-1 rounded bg-red-100 text-red-700 font-bold">rejeitada</span>}</div>
                  <Button size="sm" onClick={() => setPick(s)} className="bg-blue-600 hover:bg-blue-700" data-testid={`customer-nfe-pick-${s.id}`}>Emitir</Button>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
      {pick && <NfeEmitModal sale={{ ...pick, customer_id: customer.id }} onClose={() => { setPick(null); load(); }} onDone={() => load()} />}
    </>
  );
};
